import { returnApi } from '../../api';

import { CustomResponse } from '../../types/api/CustomResponse';

import {
  ReturnDocumentCandidate,
  ReturnEligibility,
  ReturnPreview,
  ReturnRequest,
  SpringPage,
} from '../../types/fiscal/ReturnEvent';

import { FiscalEventSubmission } from '../../types/fiscal/FiscalEvent';

// =====================================================
// 📦 Opciones de transmisión
// =====================================================

export interface ReturnEventSubmitOptions {
  ambiente?: '00' | '01';
  idEnvio?: number;
}

// =====================================================
// 🔌 Service
// =====================================================

const ReturnEventService = {
  /**
   * Busca documentos que pueden participar
   * en un Evento de Retorno. `customerId`/`startDate`/`endDate` escopan
   * la búsqueda del lado servidor (p. ej. al agregar más documentos a un
   * evento que ya tiene un cliente seleccionado).
   *
   * GET /api/dte/sales/return/documents
   */
  searchDocuments: async (
    query = '',
    page = 0,
    size = 10,
    filters: { customerId?: number; startDate?: string; endDate?: string } = {}
  ): Promise<
    CustomResponse<
      SpringPage<ReturnDocumentCandidate>
    >
  > => {
    const params = new URLSearchParams({
      query,
      page: String(page),
      size: String(Math.min(size, 20)),
    });
    if (filters.customerId != null) params.set('customerId', String(filters.customerId));
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);

    const response =
      await returnApi.fetchCustom(
        `/documents?${params.toString()}`
      );

    return response.data;
  },

  /**
   * Consulta si una venta es elegible para
   * participar en un Evento de Retorno.
   *
   * GET /api/dte/sales/return/eligibility/{saleId}
   */
  eligibility: async (
    saleId: number
  ): Promise<CustomResponse<ReturnEligibility>> => {
    const response =
      await returnApi.fetchCustom(
        `/eligibility/${saleId}`
      );

    return response.data;
  },

  /**
   * Genera la previsualización fiscal
   * antes de crear el evento.
   *
   * POST /api/dte/sales/return/preview
   */
  preview: async (
    request: ReturnRequest
  ): Promise<CustomResponse<ReturnPreview>> => {
    const response =
      await returnApi.addCustom(
        '/preview',
        request
      );

    return response.data;
  },

  /**
   * Genera y transmite el Evento de Retorno.
   *
   * POST /api/dte/sales/return
   */
  submit: async (
    request: ReturnRequest,
    idempotencyKey: string,
    options: ReturnEventSubmitOptions = {}
  ): Promise<
    CustomResponse<FiscalEventSubmission>
  > => {
    const {
      ambiente = '00',
      idEnvio = 1,
    } = options;

    const params = new URLSearchParams({
      ambiente,
      idEnvio: String(idEnvio),
    });

    const response =
      await returnApi.addCustomWithHeaders(
        `?${params.toString()}`,
        request,
        {
          'Idempotency-Key': idempotencyKey,
        }
      );

    return response.data;
  },
};

export default ReturnEventService;